import { useMemo } from "react"
import { appConstants } from "@app/core/appConstants"
import { LlmModule } from "@app/core/dto/organization/llm-usage"
import { Organization } from "@app/core/dto/organization/Organization"
import { OrganizationActivity, OrganizationConnections } from "@app/core/dto/organization/organization-activity"
import { OrganizationAdminCreateOrUpdate } from "@app/core/dto/organization/OrganizationAdminCreateOrUpdate"
import { OrganizationUpdate } from "@app/core/dto/organization/organization-update"
import { Collaborator } from "@app/core/dto/user/collaborator"
import { streamToBlob } from "@app/core/services/file-service"
import { useHttp } from "./use-http"

interface OrganizationHook {
  fetchOrganizationsList(): Promise<Organization[]>
  fetchMyOrganization(): Promise<Organization | undefined>
  adminFetchOrganization(organizationId: string): Promise<Organization>
  /** US 2554 — blocks or reopens the organization and marks it as a customer lost or not, by hand. */
  adminUpdateOrganizationStatus(
    organizationId: string,
    status: { organizationBlocked: boolean; churn: boolean }
  ): Promise<Organization>
  /**
   * US 2597 — the envelope Time To Beem grants an organization on one AI module, in dollars. No hard limit applies
   * here, which is why it is the way an organization goes above the one it may grant itself; refused by the server
   * below what the organization has already handed out to its regions and agencies.
   */
  adminUpdateLlmBudget(organizationId: string, module: LlmModule, maxBudgetUsd: number): Promise<void>
  /**
   * US 2597 — the budget the organization grants itself on one AI module, in dollars, which we re-invoice to it.
   * Reserved to its administrator and refused above ORGANIZATION_BUDGET_HARD_LIMIT_USD, with
   * LLM_BUDGET_OVER_HARD_LIMIT: going further is a conversation with the support.
   */
  updateMyOrganizationLlmBudget(module: LlmModule, maxBudgetUsd: number): Promise<void>
  /** US 2554 — the use every organization makes of the platform: last trace of use, journey, users. */
  fetchOrganizationsActivity(): Promise<OrganizationActivity[]>
  /** US 2554 — who of the organization signed in, and when. */
  fetchOrganizationConnections(organizationId: string): Promise<OrganizationConnections>
  /** US 2554 — blocks or reopens the access of one collaborator. */
  adminSetCollaboratorBlocked(userId: string, blocked: boolean): Promise<void>
  updateOrganization(organization: OrganizationUpdate): Promise<Organization>
  fetchCollaborators(): Promise<Collaborator[]>
  /**
   * US 2596: the collaborators the current user administers — the whole organization for an organization
   * administrator, the agencies they hold for a region or an agency administrator. Refused to everyone else, so it
   * serves the administration tab alone; a project team and a gamme team staff themselves from the colleagues of
   * fetchCollaborators instead.
   */
  fetchAdministeredCollaborators(): Promise<Collaborator[]>
  /** An organization is created by the creation steps (ORGANIZATION_CREATION_URL), never here. */
  updateOrganizationAsUltimate(organization: OrganizationAdminCreateOrUpdate): Promise<Organization>
  getCollaboratorsForOrganization(organizationId: string): Promise<Collaborator[]>
  /** Logo printed on the ACV report. Administrators only — the backend enforces it (US 2370). */
  uploadOrganizationLogo(logoFile: File): Promise<Organization>
  /** Undefined when the organization has no logo: the backend answers 204, not an error. */
  fetchOrganizationLogo(): Promise<Blob | undefined>
  deleteOrganizationLogo(): Promise<Organization>
}

export function useOrganization(): OrganizationHook {
  const { get, put, postFile, deleteRequest } = useHttp()

  return useMemo(
    () => ({
      fetchOrganizationsList(): Promise<Organization[]> {
        return get(`${appConstants.apiEndpoints.ORGANIZATION}/organization-list`).then((response) => response.json())
      },
      fetchMyOrganization(): Promise<Organization | undefined> {
        return get(`${appConstants.apiEndpoints.ORGANIZATION}/my-organization`).then((response) => {
          if (response.status === 401) {
            return undefined
          } else {
            return response.json()
          }
        })
      },
      adminFetchOrganization(organizationId: string): Promise<Organization> {
        return get(`${appConstants.apiEndpoints.ORGANIZATION}/${organizationId}`).then((response) => response.json())
      },
      adminUpdateOrganizationStatus(organizationId, status): Promise<Organization> {
        return put(
          `${appConstants.apiEndpoints.ORGANIZATION}/admin/${encodeURIComponent(organizationId)}/status`,
          status
        ).then((response) => response.json())
      },
      adminUpdateLlmBudget(organizationId, module, maxBudgetUsd): Promise<void> {
        return put(`${appConstants.apiEndpoints.ORGANIZATION}/admin/${encodeURIComponent(organizationId)}/llm-budget`, {
          module,
          maxBudgetUsd,
        }).then(() => undefined)
      },
      updateMyOrganizationLlmBudget(module, maxBudgetUsd): Promise<void> {
        return put(`${appConstants.apiEndpoints.ORGANIZATION}/my-organization/llm-budget`, {
          module,
          maxBudgetUsd,
        }).then(() => undefined)
      },
      adminSetCollaboratorBlocked(userId, blocked): Promise<void> {
        return put(
          `${appConstants.apiEndpoints.ORGANIZATION}/admin/collaborator/${encodeURIComponent(userId)}/blocked`,
          {},
          [{ key: "blocked", value: String(blocked) }]
        ).then(() => undefined)
      },
      fetchOrganizationConnections(organizationId): Promise<OrganizationConnections> {
        return get(
          `${appConstants.apiEndpoints.ORGANIZATION}/admin/${encodeURIComponent(organizationId)}/connections`
        ).then((response) => response.json())
      },
      fetchOrganizationsActivity(): Promise<OrganizationActivity[]> {
        return get(`${appConstants.apiEndpoints.ORGANIZATION}/admin/activity`).then((response) => response.json())
      },
      updateOrganization(organization): Promise<Organization> {
        return put(`${appConstants.apiEndpoints.ORGANIZATION}/my-organization`, organization).then((response) =>
          response.json()
        )
      },
      fetchCollaborators(): Promise<Collaborator[]> {
        return get(appConstants.apiEndpoints.ORGANIZATION_TEAM_COLLABORATORS).then((response) => response.json())
      },
      fetchAdministeredCollaborators(): Promise<Collaborator[]> {
        return get(appConstants.apiEndpoints.ORGANIZATION_ADMINISTERED_COLLABORATORS).then((response) =>
          response.json()
        )
      },
      updateOrganizationAsUltimate(organization: OrganizationAdminCreateOrUpdate): Promise<Organization> {
        return put(appConstants.apiEndpoints.ORGANIZATION, organization).then((response) => response.json())
      },
      getCollaboratorsForOrganization(organizationId: string): Promise<Collaborator[]> {
        return get(appConstants.apiEndpoints.ORGANIZATION_TEAM_ADMIN_COLLABORATORS, [
          {
            key: "organizationId",
            value: organizationId,
          },
        ]).then((response) => response.json())
      },
      uploadOrganizationLogo(logoFile: File): Promise<Organization> {
        const formData = new FormData()
        formData.append("file", logoFile)
        return postFile(appConstants.apiEndpoints.ORGANIZATION_LOGO, formData).then((response) => response.json())
      },
      async fetchOrganizationLogo(): Promise<Blob | undefined> {
        const response = await get(appConstants.apiEndpoints.ORGANIZATION_LOGO, [])
        // 204: the organization simply has no logo. Anything else is a real failure and
        // useHttp has already surfaced it.
        if (response.status === 204) {
          return undefined
        }
        return streamToBlob(response)
      },
      deleteOrganizationLogo(): Promise<Organization> {
        return deleteRequest(appConstants.apiEndpoints.ORGANIZATION_LOGO, []).then((response) => response.json())
      },
    }),
    [get, put, postFile, deleteRequest]
  )
}
